import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, FileClock, HardDrive, KeyRound, ScanSearch, Settings2, SlidersHorizontal, Users, Webhook } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, ep } from './lib/api.js';
import { ApiKeysTab } from './components/ApiKeysTab.jsx';
import { AuditTab } from './components/AuditTab.jsx';
import { IndexingTab } from './components/IndexingTab.jsx';
import { PoliciesTab } from './components/PoliciesTab.jsx';
import { RetentionTab } from './components/RetentionTab.jsx';
import { SettingsTab } from './components/SettingsTab.jsx';
import { StorageTab } from './components/StorageTab.jsx';
import { UsersTab } from './components/UsersTab.jsx';
import { WebhooksTab } from './components/WebhooksTab.jsx';

// Labels are i18n KEYS resolved at render — a module-level constant cannot
// hold translated text (it would freeze the language at import time).
const TABS = [
  { value: 'settings', labelKey: 'shell.tabs.settings', icon: Settings2, Component: SettingsTab },
  { value: 'users', labelKey: 'shell.tabs.users', icon: Users, Component: UsersTab },
  { value: 'policies', labelKey: 'shell.tabs.policies', icon: SlidersHorizontal, Component: PoliciesTab },
  { value: 'api-keys', labelKey: 'shell.tabs.apiKeys', icon: KeyRound, Component: ApiKeysTab },
  { value: 'webhooks', labelKey: 'shell.tabs.webhooks', icon: Webhook, Component: WebhooksTab },
  { value: 'indexing', labelKey: 'shell.tabs.indexing', icon: ScanSearch, Component: IndexingTab },
  { value: 'audit', labelKey: 'shell.tabs.audit', icon: FileClock, Component: AuditTab },
  { value: 'storage', labelKey: 'shell.tabs.storage', icon: HardDrive, Component: StorageTab },
  { value: 'retention', labelKey: 'shell.tabs.retention', icon: Archive, Component: RetentionTab },
];
const DEFAULT_TAB = TABS[0].value;
// The signing section lives in Policies — old links still land there.
const TAB_ALIASES = { signing: 'policies' };

/**
 * docs' console: the install's settings and group grants, its people,
 * policies, API keys, webhooks, indexing, audit, storage and retention. One
 * install, no projects: every section is install-wide (`/api/v1/settings…`,
 * `/api/v1/api-keys`). Who may open it is the route's gate (docs:admin or
 * the break-glass admin); the tabs follow `?tab=`.
 */
export const Console = () => {
  const { t } = useTranslation('console');
  const [searchParams, setSearchParams] = useSearchParams();
  // The install's name heads the console; the signing stamp prints it too.
  const [organizationName, setOrganizationName] = useState('');

  const requestedTab = TAB_ALIASES[searchParams.get('tab')] || searchParams.get('tab');
  const tab = TABS.some((item) => item.value === requestedTab) ? requestedTab : DEFAULT_TAB;
  // Tabs mount lazily — only what the admin opened fetches — and stay
  // mounted once visited, so switching back keeps state (page, filters, a
  // running rebuild's card) without a refetch.
  const [visited, setVisited] = useState(() => new Set([tab]));
  useEffect(() => {
    setVisited((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);

  useEffect(() => {
    let cancelled = false;
    api.get(ep.settings()).then(({ success, response }) => {
      if (!cancelled && success) setOrganizationName(response?.organization_name || '');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setTab = (value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1 min-w-0">
          {organizationName && (
            <p className="text-sm font-medium text-muted-foreground mb-1 truncate" data-testid="console-organization">
              {organizationName}
            </p>
          )}
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('shell.title')}</h1>
          <p className="text-muted-foreground text-base">{t('shell.tagline')}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <div className="mb-4 -mx-1 overflow-x-auto px-1">
          <TabsList className="h-10 w-max">
            {TABS.map((tabItem) => (
              <TabsTrigger key={tabItem.value} value={tabItem.value} className="px-3 sm:px-4 gap-2" title={t(tabItem.labelKey)}>
                <tabItem.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{t(tabItem.labelKey)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {TABS.map((tabItem) => (
          <TabsContent
            key={tabItem.value}
            value={tabItem.value}
            forceMount={visited.has(tabItem.value) ? true : undefined}
            className={tabItem.value === tab ? '' : 'hidden'}
          >
            {visited.has(tabItem.value) && (
              <tabItem.Component organizationName={organizationName} onSettingsSaved={(s) => setOrganizationName(s?.organization_name || '')} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
